import { useState, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { N8N_WEBHOOKS, createFormDataRequest, createJSONRequest, handleAPIResponse, apiLogger } from '../config/api'
import { 
  ArrowLeft, 
  Upload, 
  Calculator, 
  Clock, 
  CheckCircle, 
  AlertTriangle,
  XCircle,
  Download,
  FileText,
  TrendingUp,
  LogOut,
  Play,
  Pause,
  RotateCcw,
  Eye,
  Filter
} from 'lucide-react'
import toast from 'react-hot-toast'

export default function ConciliacionDemo({ onLogout }) {
  const [bancoFile, setBancoFile] = useState(null)
  const [contabilidadFile, setContabilidadFile] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [results, setResults] = useState(null)
  const [progress, setProgress] = useState(0)
  const [showDetails, setShowDetails] = useState(false)
  const [filterType, setFilterType] = useState('all')
  
  const bancoInputRef = useRef(null)
  const contabilidadInputRef = useRef(null)

  // Estado para manejar errores de procesamiento
  const [processingError, setProcessingError] = useState(null)
  const [errorLogs, setErrorLogs] = useState([])

  // Helper function to convert file to base64
  const convertFileToBase64 = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        // Remove the data URL prefix and keep only base64 data
        const base64 = reader.result.split(',')[1]
        resolve(base64)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  const handleFileUpload = useCallback((type, file) => {
    if (type === 'banco') {
      // Para banco aceptamos PDF
      if (file && file.type === 'application/pdf') {
        setBancoFile(file)
        toast.success('Estado de cuenta PDF cargado correctamente')
      } else {
        toast.error('Por favor selecciona un archivo PDF válido para el estado de cuenta')
      }
    } else {
      // Para contabilidad aceptamos CSV o Excel
      if (file && (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel' || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')) {
        setContabilidadFile(file)
        toast.success('Archivo de contabilidad cargado correctamente')
      } else {
        toast.error('Por favor selecciona un archivo CSV o Excel válido para los registros contables')
      }
    }
  }, [])

  const processPDFAndConciliate = async () => {
    setIsProcessing(true)
    setProgress(0)
    setProcessingError(null)
    setErrorLogs([])
    
    try {
      // Paso 1: Procesar PDF bancario con OCR
      setProgress(15)
      toast.loading('Extrayendo datos del PDF con OCR...', { id: 'processing' })
      
                    // Convertir archivo a base64 para envío JSON
       const fileReader = new FileReader()
       const fileBase64 = await new Promise((resolve, reject) => {
         fileReader.onload = () => resolve(fileReader.result)
         fileReader.onerror = reject
         fileReader.readAsDataURL(bancoFile)
       })
       
       // Crear payload JSON para N8N
       const pdfPayload = {
         file_type: bancoFile.type,
         filename: bancoFile.name,
         file_size: bancoFile.size,
         file_data: fileBase64.split(',')[1], // Solo la parte base64, sin el prefijo data:
         content_type: bancoFile.type
       }
       
       // Debug: Verificar que estamos enviando los datos correctos
       console.log('📤 JSON payload being sent:', {
         file_type: pdfPayload.file_type,
         filename: pdfPayload.filename,
         file_size: pdfPayload.file_size,
         content_type: pdfPayload.content_type,
         file_data_length: pdfPayload.file_data.length
       })
       
       // Llamar al workflow de procesamiento de PDF
       console.log('🔄 Calling PDF processing webhook...')
       apiLogger.request(N8N_WEBHOOKS.PDF_PROCESSING, { method: 'POST', body: pdfPayload })
       
       const pdfResponse = await createJSONRequest(N8N_WEBHOOKS.PDF_PROCESSING, pdfPayload)
       console.log('📦 PDF Response received:', pdfResponse)
       
       const pdfResult = await handleAPIResponse(pdfResponse)
       console.log('✅ PDF Result:', pdfResult)
       apiLogger.response(N8N_WEBHOOKS.PDF_PROCESSING, pdfResult)
      
      setProgress(40)
      toast.loading('Procesando registros contables...', { id: 'processing' })
      
      // Paso 2: Procesar archivo de contabilidad
      const contabilidadData = new FormData()
      contabilidadData.append('file', contabilidadFile)
      
      // Simular procesamiento de contabilidad
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      setProgress(60)
      toast.loading('Ejecutando algoritmos de conciliación IA...', { id: 'processing' })
      
      // Paso 3: Ejecutar conciliación automática
      const conciliationPayload = {
        pdf_processed: true,
        accounting_file_data: await convertFileToBase64(contabilidadFile),
        accounting_filename: contabilidadFile.name,
        pdf_result: pdfResult
      }
      
      apiLogger.request(N8N_WEBHOOKS.CONCILIATION, { method: 'POST', body: conciliationPayload })
      const conciliationResponse = await createJSONRequest(N8N_WEBHOOKS.CONCILIATION, conciliationPayload)
      const conciliationData = await handleAPIResponse(conciliationResponse)
      apiLogger.response(N8N_WEBHOOKS.CONCILIATION, conciliationData)
      
      setProgress(85)
      toast.loading('Generando reportes finales...', { id: 'processing' })
      
      setProgress(100)
      toast.dismiss('processing')
      
      // Usar SOLO datos reales de la respuesta
      console.log('✅ Conciliation data received:', conciliationData)
      
      if (conciliationData && conciliationData.results_json) {
        // Parsear el JSON string que viene en results_json
        const parsedResults = JSON.parse(conciliationData.results_json)
        
        // Crear el objeto de resultados con la estructura esperada por el componente
        const formattedResults = {
          summary: {
            matchedTransactions: conciliationData.matched_transactions || parsedResults.summary?.matchedTransactions || 0,
            discrepancies: conciliationData.discrepancies || parsedResults.summary?.discrepancies || 0,
            accuracy: conciliationData.accuracy || parsedResults.summary?.accuracy || '0',
            timeSaved: conciliationData.time_saved || parsedResults.summary?.timeSaved || '0m',
            totalTransactions: conciliationData.total_transactions || parsedResults.summary?.totalTransactions || 0,
            unmatchedTransactions: parsedResults.summary?.unmatchedTransactions || 0,
            bankTransactions: parsedResults.summary?.bankTransactions || 0,
            accountingTransactions: parsedResults.summary?.accountingTransactions || 0
          },
                     transactions: (parsedResults.transactions || []).map((tx, index) => ({
             ...tx,
             id: `${tx.id}_${index}` // Asegurar IDs únicos
           })),
          metadata: {
            id: conciliationData.id,
            created_at: conciliationData.created_at,
            bank_file: conciliationData.bank_file,
            accounting_file: conciliationData.accounting_file,
            processing_method: conciliationData.processing_method,
            status: conciliationData.status,
            ai_details: parsedResults.ai_details
          }
        }
        
        console.log('✅ Formatted results:', formattedResults)
        setResults(formattedResults)
        toast.success('Conciliación completada exitosamente con datos reales')
      } else {
        // NO usar datos de ejemplo - mostrar error real
        console.error('❌ No se recibieron datos válidos del servidor')
        console.error('Response structure:', conciliationData)
        throw new Error('El servidor no devolvió datos de conciliación válidos. Verifica que el campo results_json esté presente.')
      }
      
    } catch (error) {
      apiLogger.error('processPDFAndConciliate', error)
      toast.dismiss('processing')
      setProgress(0)
      
             // Log detallado del error
       const errorLog = {
         message: error.message,
         stack: error.stack,
         timestamp: new Date().toISOString(),
         bancoFile: bancoFile ? { name: bancoFile.name, type: bancoFile.type, size: bancoFile.size } : null,
         contabilidadFile: contabilidadFile ? { name: contabilidadFile.name, type: contabilidadFile.type, size: contabilidadFile.size } : null
       }
       
       console.error('🔥 ERROR COMPLETO:', errorLog)
       setErrorLogs(prev => [...prev, errorLog])
       
       // Determinar tipo de error y mostrar mensaje específico
       let errorMessage = 'Error desconocido en el procesamiento'
       
       if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
         errorMessage = 'Error de conectividad: Verifica que N8N esté configurado para permitir CORS desde localhost:3000'
       } else if (error.message.includes('500')) {
         errorMessage = 'Error interno del servidor (500). Revisa los logs de N8N y PostgreSQL.'
       } else if (error.message.includes('404')) {
         errorMessage = 'Webhook no encontrado (404). Verifica que los workflows estén importados en N8N.'
       } else if (error.message.includes('400')) {
         errorMessage = 'Error de validación (400). El archivo PDF no es válido o el formato no es correcto.'
       } else if (error.message.includes('Archivo no válido')) {
         errorMessage = 'El archivo PDF no pasó la validación. Verifica que sea un PDF válido y no esté corrupto.'
       } else {
         errorMessage = `Error en el procesamiento: ${error.message}`
       }
       
       setProcessingError(errorMessage)
       toast.error(errorMessage)
       
       // NO usar datos de ejemplo - dejar results como null para mostrar estado de error
       setResults(null)
       
       // Mostrar estado de error en lugar de datos falsos
       console.log('❌ Proceso fallido - NO se mostrarán datos de demostración')
       toast.error('Proceso cancelado. Revisa los logs de error para más detalles.')
      
    } finally {
      setIsProcessing(false)
    }
  }

  const testWebhookConnection = async () => {
    try {
      console.log('🧪 Testing webhook connection...')
      const testPayload = { test: true, timestamp: new Date().toISOString() }
      
      console.log('📤 Sending test to PDF webhook:', N8N_WEBHOOKS.PDF_PROCESSING)
      const response = await createJSONRequest(N8N_WEBHOOKS.PDF_PROCESSING, testPayload)
      console.log('📦 Test response:', response)
      
      const result = await handleAPIResponse(response)
      console.log('✅ Test result:', result)
      
      toast.success('Webhook connection successful!')
    } catch (error) {
      console.error('❌ Webhook test failed:', error)
      toast.error(`Webhook test failed: ${error.message}`)
    }
  }

  const handleStartProcessing = () => {
    if (!bancoFile || !contabilidadFile) {
      toast.error('Por favor carga ambos archivos antes de continuar')
      return
    }
    processPDFAndConciliate()
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'matched':
        return 'bg-green-100 text-green-800 border-green-200'
      case 'discrepancy':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200'
      case 'unmatched_banco':
      case 'unmatched_contabilidad':
        return 'bg-red-100 text-red-800 border-red-200'
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched':
        return <CheckCircle className="w-4 h-4" />
      case 'discrepancy':
        return <AlertTriangle className="w-4 h-4" />
      case 'unmatched_banco':
      case 'unmatched_contabilidad':
        return <XCircle className="w-4 h-4" />
      default:
        return null
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'matched':
        return 'Conciliado'
      case 'discrepancy':
        return 'Discrepancia'
      case 'unmatched_banco':
        return 'Solo en Banco'
      case 'unmatched_contabilidad':
        return 'Solo en Contabilidad'
      default:
        return 'Desconocido'
    }
  }

  const filteredTransactions = results?.transactions.filter(transaction => {
    if (filterType === 'all') return true
    return transaction.status === filterType
  }) || []

  const formatCurrency = (amount) => {
    if (amount === null) return 'N/A'
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(Math.abs(amount))
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm border-b flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors mr-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver
              </Link>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-lg flex items-center justify-center mr-3">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">Conciliación Automática AI</h1>
                  <p className="text-sm text-gray-500">Sistema Inteligente de Conciliación</p>
                </div>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl mx-auto w-full p-6">
        {!results ? (
          // Upload Section
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            {/* Instructions */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Instrucciones</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Upload className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">1. Cargar Archivos</h3>
                  <p className="text-sm text-gray-600">Sube tu estado de cuenta bancario y registros contables en formato CSV o Excel</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <Calculator className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">2. Procesamiento AI</h3>
                  <p className="text-sm text-gray-600">Nuestra IA analiza y concilia automáticamente las transacciones</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">3. Revisar Resultados</h3>
                  <p className="text-sm text-gray-600">Obtén un reporte detallado con matches, discrepancias y recomendaciones</p>
                </div>
              </div>
            </div>

            {/* File Upload Cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Banco File Upload */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  Estado de Cuenta Bancario
                </h3>
                
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    bancoFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => bancoInputRef.current?.click()}
                >
                                     <input
                     ref={bancoInputRef}
                     type="file"
                     accept=".pdf"
                     onChange={(e) => handleFileUpload('banco', e.target.files[0])}
                     className="hidden"
                   />
                  
                  {bancoFile ? (
                    <div className="text-green-600">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                      <p className="font-medium">{bancoFile.name}</p>
                      <p className="text-sm mt-1">Archivo cargado correctamente</p>
                    </div>
                  ) : (
                    <div className="text-gray-400">
                      <Upload className="w-12 h-12 mx-auto mb-3" />
                      <p className="font-medium">Haz clic para cargar PDF</p>
                      <p className="text-sm mt-1">Estado de cuenta PDF (máx. 10MB)</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Contabilidad File Upload */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mr-3">
                    <Calculator className="w-4 h-4 text-emerald-600" />
                  </div>
                  Registros Contables
                </h3>
                
                <div 
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    contabilidadFile ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onClick={() => contabilidadInputRef.current?.click()}
                >
                  <input
                    ref={contabilidadInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => handleFileUpload('contabilidad', e.target.files[0])}
                    className="hidden"
                  />
                  
                  {contabilidadFile ? (
                    <div className="text-green-600">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                      <p className="font-medium">{contabilidadFile.name}</p>
                      <p className="text-sm mt-1">Archivo cargado correctamente</p>
                    </div>
                  ) : (
                    <div className="text-gray-400">
                      <Upload className="w-12 h-12 mx-auto mb-3" />
                      <p className="font-medium">Haz clic para cargar archivo</p>
                      <p className="text-sm mt-1">CSV o Excel (máx. 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Process Button */}
            <div className="text-center">
              <button
                onClick={handleStartProcessing}
                disabled={!bancoFile || !contabilidadFile || isProcessing}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white px-8 py-3 rounded-lg font-medium transition-colors disabled:cursor-not-allowed flex items-center mx-auto"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Procesando...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Iniciar Conciliación
                  </>
                )}
              </button>

              {/* Progress Bar */}
              {isProcessing && (
                <div className="mt-6 max-w-md mx-auto">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-emerald-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">{progress}% completado</p>
                </div>
              )}
            </div>
          </motion.div>
                 ) : processingError ? (
           // Error Section - Mostrar cuando hay errores
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             className="space-y-6"
           >
             {/* Error Card */}
             <div className="bg-red-50 border border-red-200 rounded-xl p-6">
               <div className="flex items-center mb-4">
                 <XCircle className="w-8 h-8 text-red-500 mr-3" />
                 <div>
                   <h2 className="text-xl font-semibold text-red-800">Error en el Procesamiento</h2>
                   <p className="text-red-600">No se pudieron procesar los archivos</p>
                 </div>
               </div>
               
               <div className="bg-white rounded-lg p-4 border border-red-200">
                 <p className="text-red-700 font-medium mb-2">Mensaje de Error:</p>
                 <p className="text-red-600">{processingError}</p>
               </div>
               
               {errorLogs.length > 0 && (
                 <div className="mt-4">
                   <details className="bg-white rounded-lg border border-red-200">
                     <summary className="p-4 cursor-pointer font-medium text-red-700 hover:bg-red-50">
                       Ver Logs Técnicos ({errorLogs.length})
                     </summary>
                     <div className="p-4 border-t border-red-200 bg-gray-50">
                       {errorLogs.map((log, index) => (
                         <div key={index} className="mb-4 last:mb-0">
                           <div className="text-xs text-gray-500 mb-1">
                             {log.timestamp}
                           </div>
                           <div className="bg-red-100 p-3 rounded font-mono text-sm text-red-800">
                             <div><strong>Error:</strong> {log.message}</div>
                             {log.bancoFile && (
                               <div><strong>Archivo Banco:</strong> {log.bancoFile.name} ({log.bancoFile.type})</div>
                             )}
                             {log.contabilidadFile && (
                               <div><strong>Archivo Contabilidad:</strong> {log.contabilidadFile.name} ({log.contabilidadFile.type})</div>
                             )}
                           </div>
                         </div>
                       ))}
                     </div>
                   </details>
                 </div>
               )}
               
               <div className="mt-6 flex space-x-4">
                 <button
                   onClick={() => {
                     setProcessingError(null)
                     setErrorLogs([])
                     setBancoFile(null)
                     setContabilidadFile(null)
                     setProgress(0)
                   }}
                   className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                 >
                   <RotateCcw className="w-4 h-4 mr-2" />
                   Intentar de Nuevo
                 </button>
                 
                 <button
                   onClick={() => {
                     console.log('🔍 DEBUGGING INFO:')
                     console.log('Error Logs:', errorLogs)
                     console.log('Processing Error:', processingError)
                     console.log('N8N Webhooks:', N8N_WEBHOOKS)
                     toast.success('Información de debug enviada a la consola')
                   }}
                   className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                 >
                   <FileText className="w-4 h-4 mr-2" />
                   Debug Info
                 </button>
               </div>
             </div>
           </motion.div>
         ) : results ? (
           // Results Section - Solo mostrar cuando hay datos reales
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             className="space-y-6"
           >
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center">
                  <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Conciliadas</p>
                    <p className="text-xl font-bold text-gray-900">{results.summary.matchedTransactions}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center">
                  <AlertTriangle className="w-8 h-8 text-yellow-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Discrepancias</p>
                    <p className="text-xl font-bold text-gray-900">{results.summary.discrepancies}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center">
                  <TrendingUp className="w-8 h-8 text-blue-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Precisión</p>
                    <p className="text-xl font-bold text-gray-900">{results.summary.accuracy}%</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border">
                <div className="flex items-center">
                  <Clock className="w-8 h-8 text-purple-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600">Tiempo Ahorrado</p>
                    <p className="text-xl font-bold text-gray-900">{results.summary.timeSaved}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions Bar */}
            <div className="bg-white rounded-lg border p-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setShowDetails(!showDetails)}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    {showDetails ? 'Ocultar' : 'Ver'} Detalles
                  </button>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                    >
                      <option value="all">Todas las transacciones</option>
                      <option value="matched">Solo conciliadas</option>
                      <option value="discrepancy">Solo discrepancias</option>
                      <option value="unmatched_banco">Solo banco</option>
                      <option value="unmatched_contabilidad">Solo contabilidad</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                                     <button
                     onClick={() => {
                       setResults(null)
                       setBancoFile(null)
                       setContabilidadFile(null)
                       setProgress(0)
                       setProcessingError(null)
                       setErrorLogs([])
                     }}
                     className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                   >
                     <RotateCcw className="w-4 h-4 mr-2" />
                     Nueva Conciliación
                   </button>
                  
                  <button className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar Reporte
                  </button>
                </div>
              </div>
            </div>

            {/* Transactions Table */}
            {showDetails && (
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Detalle de Transacciones ({filteredTransactions.length})
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Banco</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Contabilidad</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Diferencia</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Confianza</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredTransactions.map((transaction) => (
                        <tr key={transaction.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.fecha}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {transaction.concepto}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                            {formatCurrency(transaction.montoBanco)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                            {formatCurrency(transaction.montoContabilidad)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                            {transaction.diferencia !== 0 ? formatCurrency(transaction.diferencia) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(transaction.status)}`}>
                              {getStatusIcon(transaction.status)}
                              <span className="ml-1">{getStatusText(transaction.status)}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                            {transaction.confidence > 0 ? `${transaction.confidence}%` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                             </div>
             )}
           </motion.div>
         ) : (
           // Estado inicial - sin resultados ni errores
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             className="text-center py-12"
           >
             <div className="text-gray-400">
               <Calculator className="w-16 h-16 mx-auto mb-4" />
               <p className="text-lg font-medium">Esperando resultados...</p>
               <p className="text-sm">Los resultados aparecerán aquí una vez que el procesamiento sea exitoso</p>
             </div>
           </motion.div>
         )}
       </div>
     </div>
   )
 } 